import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageTitle from "@/hooks/use-page-title";
import { ArrowRight, Search, Sparkles, MessageCircle } from "lucide-react";
import { getDiscountInfo } from "@/lib/discountUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ACCENTS = {
  "Business Cards": "from-orange-500 to-red-500",
  "Business Stationery": "from-blue-600 to-indigo-700",
  "Marketing Materials": "from-emerald-500 to-teal-600",
  "Label & Packaging": "from-amber-500 to-orange-600",
  "Indoor & Outdoor": "from-purple-600 to-pink-600",
  "Personalised & Gifting": "from-rose-500 to-fuchsia-600",
};

export default function Products() {
  usePageTitle("Products");
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [group, setGroup] = useState(params.get("g") || "");
  const [q, setQ] = useState(params.get("q") || "");

  // Sync internal state whenever the URL's ?g= / ?q= changes (e.g. user clicks
  // a different group in the header strip while already on this page).
  useEffect(() => {
    setGroup(params.get("g") || "");
    setQ(params.get("q") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    fetch(`${API}/catalog?active_only=true`).then((r) => r.json()).then(setCatalog).catch(() => setCatalog([]));
    fetch(`${API}/media?category=product`).then((r) => r.json()).then(setPhotos).catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    if (group) next.set("g", group);
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  }, [group, q, setParams]);

  const groups = useMemo(() => {
    const s = new Set();
    catalog.forEach((c) => {
      if (c.group) s.add(c.group);
      (c.groups || []).forEach((g) => g && s.add(g));
    });
    return Array.from(s);
  }, [catalog]);
  const filtered = useMemo(() => catalog.filter((c) => {
    const inGroup = !group || c.group === group || (c.groups || []).includes(group);
    const hay = `${c.name} ${c.category} ${(c.categories || []).join(" ")} ${(c.tags || []).join(" ")}`.toLowerCase();
    return inGroup && (!q || hay.includes(q.toLowerCase()));
  }), [catalog, group, q]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-orange-50 via-white to-orange-100" />
        <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-[#FF6B00]/20 blur-3xl -z-10" />
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <div className="overline mb-3 flex items-center justify-center gap-2 text-[#FF6B00]">
            <Sparkles size={14} /> {catalog.length}+ products · 6 categories
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-none">
            Our <span className="text-[#FF6B00]">Print</span> Range
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl mx-auto">
            Signage, large format, corporate stationery, promotional gifts — indicative starting prices below. Final price depends on size, material and quantity.
          </p>
          <div className="mt-8 max-w-lg mx-auto flex items-center border-2 border-zinc-200 focus-within:border-[#FF6B00] bg-white rounded-full px-4 py-3">
            <Search size={18} className="text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products — flex, cards, sign board…"
              className="flex-1 px-3 bg-transparent outline-none text-sm" data-testid="product-search-input" />
          </div>
        </div>
      </section>

      {/* Group filter chips */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 sticky top-16 z-20 bg-white/85 backdrop-blur-xl py-4 border-b border-zinc-100">
        <div className="flex flex-wrap items-center gap-2" data-testid="group-filters">
          <button
            onClick={() => setGroup("")}
            className={`text-xs font-bold px-3 py-2 rounded-full border-2 ${!group ? "bg-[#FF6B00] text-white border-[#FF6B00]" : "border-zinc-200 hover:border-[#FF6B00]"}`}
            data-testid="filter-all"
          >
            All ({catalog.length})
          </button>
          {groups.map((g) => (
            <button
              key={g}
              onClick={() => setGroup(g === group ? "" : g)}
              className={`text-xs font-bold px-3 py-2 rounded-full border-2 ${g === group ? "bg-[#FF6B00] text-white border-[#FF6B00]" : "border-zinc-200 hover:border-[#FF6B00]"}`}
              data-testid={`filter-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {g} ({catalog.filter((c) => c.group === g || (c.groups || []).includes(g)).length})
            </button>
          ))}
        </div>
      </section>

      {/* Featured work (admin uploads) */}
      {photos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <div className="overline mb-1 text-[#FF6B00]">Real work</div>
              <h2 className="font-display text-3xl font-black tracking-tighter">Recent jobs delivered</h2>
            </div>
            <Link to="/portfolio" className="text-sm font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1">
              View portfolio <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.slice(0, 8).map((p) => (
              <div key={p.id} className="relative aspect-square overflow-hidden rounded-2xl border-2 border-zinc-200 group">
                <img src={`${API}/media/${p.id}`} alt={p.label || "Product"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-3">
                  <div className="text-white text-sm font-bold">{p.label || "Featured"}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Product grid */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="overline mb-1">{group || "All products"}</div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
              {filtered.length} product{filtered.length !== 1 ? "s" : ""}
            </h2>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-zinc-200 rounded-2xl">
            <div className="font-semibold text-zinc-700">No products matched "{q}"</div>
            <Link to="/contact" className="mt-3 inline-block text-[#FF6B00] font-bold">Ask us — we probably make it →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="product-grid">
            {filtered.map((p) => {
              const accent = GROUP_ACCENTS[p.group] || "from-zinc-500 to-zinc-700";
              const startUnit = p.quantity_tiers?.[0]?.unit_price ?? p.base_price;
              const startsFrom = Math.round(startUnit * (p.min_quantity || 1));
              const discount = getDiscountInfo(p, startUnit);
              const mrpTotal = discount.hasDiscount ? Math.round(discount.mrp * (p.min_quantity || 1)) : 0;
              return (
                <Link to={`/products/${p.id}`} key={p.id} className="group rounded-2xl overflow-hidden border-2 border-zinc-200 hover:border-[#FF6B00] transition-all hover:-translate-y-1 hover:shadow-xl bg-white flex flex-col" data-testid={`catalog-product-${p.id}`}>
                  {p.image_url ? (
                    <div className="aspect-[4/3] w-full relative overflow-hidden">
                      <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = "none"; }} />
                      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-white font-bold drop-shadow-lg bg-black/40 px-2 py-0.5 rounded backdrop-blur">{p.category}</div>
                      {discount.hasDiscount && <div className="absolute top-3 right-3 text-[10px] uppercase font-black text-white bg-red-600 px-2 py-1 rounded-full shadow-lg" data-testid={`discount-badge-${p.id}`}>{discount.pct}% OFF</div>}
                      {!discount.hasDiscount && p.trending && <div className="absolute top-3 right-3 text-[10px] uppercase font-bold text-white bg-[#FF6B00] px-2 py-0.5 rounded-full">Trending</div>}
                      {!discount.hasDiscount && p.best_seller && !p.trending && <div className="absolute top-3 right-3 text-[10px] uppercase font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full">Best Seller</div>}
                    </div>
                  ) : (
                    <div className={`aspect-[4/3] w-full bg-gradient-to-br ${accent} flex items-center justify-center relative`}>
                      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-white/80 font-bold">{p.category}</div>
                      <div className="text-white font-display text-2xl md:text-3xl font-black text-center px-4 leading-tight">{p.name}</div>
                      {discount.hasDiscount && <div className="absolute top-3 right-3 text-[10px] uppercase font-black text-white bg-red-600 px-2 py-1 rounded-full shadow-lg">{discount.pct}% OFF</div>}
                      {!discount.hasDiscount && p.trending && (
                        <div className="absolute top-3 right-3 text-[10px] uppercase font-bold text-white bg-black/40 px-2 py-0.5 rounded-full backdrop-blur">Trending</div>
                      )}
                      {!discount.hasDiscount && p.best_seller && !p.trending && (
                        <div className="absolute top-3 right-3 text-[10px] uppercase font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full">Best Seller</div>
                      )}
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="font-bold text-lg leading-tight group-hover:text-[#FF6B00]">{p.name}</div>
                    {p.subtitle && <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{p.subtitle}</p>}
                    <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                      <div className="text-[10px] uppercase font-bold text-zinc-500">Starts from</div>
                      <div className="font-mono text-[#FF6B00] font-black text-xl">₹{startsFrom.toLocaleString("en-IN")}</div>
                      {discount.hasDiscount && (
                        <div className="font-mono text-zinc-400 line-through text-sm" data-testid={`mrp-${p.id}`}>₹{mrpTotal.toLocaleString("en-IN")}</div>
                      )}
                    </div>
                    <div className="text-[10px] text-emerald-700 font-bold mt-0.5">Incl. of {p.gst_rate ?? 18}% GST</div>
                    <div className="mt-auto pt-4 flex items-center justify-between text-sm">
                      <span className="font-bold text-[#FF6B00] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        View & Customize <ArrowRight size={14} />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(`https://wa.me/918070970980?text=${encodeURIComponent(`Hi, I want a quote for ${p.name}`)}`, "_blank", "noopener,noreferrer");
                        }}
                        className="text-xs inline-flex items-center gap-1 text-[#25D366] hover:underline cursor-pointer"
                      >
                        <MessageCircle size={12} /> WhatsApp
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-r from-[#FF6B00] to-orange-500 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Don't see what you need?</h2>
          <p className="mt-3 text-white/90 text-lg">Custom jobs are our favourite. Send us your idea — we'll design, print and deliver.</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-white text-[#FF6B00] font-bold px-6 py-3">
            Request Custom Quote <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
